import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaBug, FaCode, FaServer, FaLock, FaNetworkWired, FaRobot, FaSkull, FaTerminal } from 'react-icons/fa';
import { allCommands, getCommandsByCategory } from '../data/cyberCommands';
import { LiveCommandFeed, CommandTicker } from '../components/CommandStream';

// ===== TYPING TERMINAL COMPONENT =====
const TypingText = ({ lines, onComplete }) => {
  const [displayedLines, setDisplayedLines] = useState([]);
  const [currentLine, setCurrentLine] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (currentLine >= lines.length) {
      if (onComplete) onComplete();
      return;
    }
    const line = lines[currentLine];

    // Handle empty lines — add them immediately and advance
    if (line.text.length === 0) {
      setDisplayedLines((prev) => {
        const newLines = [...prev];
        newLines[currentLine] = { ...line, text: '' };
        return newLines;
      });
      const delay = line.delay || 100;
      const timeout = setTimeout(() => {
        setCurrentLine(currentLine + 1);
        setCurrentChar(0);
      }, delay);
      return () => clearTimeout(timeout);
    }

    if (currentChar < line.text.length) {
      const speed = line.speed || 40;
      const timeout = setTimeout(() => {
        setDisplayedLines((prev) => {
          const newLines = [...prev];
          if (!newLines[currentLine]) newLines[currentLine] = { ...line, text: '' };
          newLines[currentLine] = { ...newLines[currentLine], text: line.text.slice(0, currentChar + 1) };
          return newLines;
        });
        setCurrentChar(currentChar + 1);
      }, speed + Math.random() * 20);
      return () => clearTimeout(timeout);
    } else {
      const delay = line.delay || 200;
      const timeout = setTimeout(() => {
        setCurrentLine(currentLine + 1);
        setCurrentChar(0);
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [currentLine, currentChar, lines, onComplete]);

  useEffect(() => {
    const interval = setInterval(() => setShowCursor((v) => !v), 530);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="font-mono text-sm md:text-base">
      {displayedLines.map((line, i) => {
        if (!line) return null;
        return (
        <div key={i} className={`${line.className || 'text-cyber-green'} mb-0.5`}>
          {line.prefix && <span className="text-cyber-muted">{line.prefix}</span>}
          {line.text}
          {i === currentLine && currentLine < lines.length && (
            <span className={`text-cyber-green ${showCursor ? 'opacity-100' : 'opacity-0'}`}>█</span>
          )}
        </div>
        );
      })}
      {currentLine >= lines.length && (
        <span className={`text-cyber-green ${showCursor ? 'opacity-100' : 'opacity-0'}`}>█</span>
      )}
    </div>
  );
};

// ===== FLOATING BACKGROUND ICONS =====
const FloatingIcon = ({ icon: Icon, delay, x, y }) => (
  <motion.div
    className="absolute text-cyber-green/10 text-4xl md:text-6xl"
    style={{ left: `${x}%`, top: `${y}%` }}
    animate={{ y: [0, -20, 0], rotate: [0, 5, -5, 0], opacity: [0.05, 0.15, 0.05] }}
    transition={{ duration: 6, delay, repeat: Infinity, ease: 'easeInOut' }}
  >
    <Icon />
  </motion.div>
);

// ===== STAT CARD =====
const StatCard = ({ value, label, icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    viewport={{ once: true }}
    className="cyber-card p-6 text-center group"
  >
    <Icon className="text-3xl text-cyber-green mx-auto mb-3 group-hover:text-cyber-cyan transition-colors" />
    <div className="text-3xl font-bold text-white mb-1">{value}</div>
    <div className="text-sm text-cyber-muted">{label}</div>
  </motion.div>
);

const Home = () => {
  const [bootComplete, setBootComplete] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Real cybersecurity commands in the boot sequence
  const bootLines = [
    { text: '> Initializing secure connection...', className: 'text-cyber-muted', speed: 25, delay: 150 },
    { text: '> iptables -A INPUT -p tcp --dport 22 -j ACCEPT', className: 'text-cyber-green/70', speed: 20, delay: 100 },
    { text: '> Loading kernel modules... [OK]', className: 'text-cyber-green', speed: 20, delay: 100 },
    { text: '> snort -A console -q -c /etc/snort/snort.conf -i eth0', className: 'text-cyber-green/70', speed: 18, delay: 100 },
    { text: '> suricata -c /etc/suricata/suricata.yaml -i eth0', className: 'text-cyber-green/70', speed: 18, delay: 100 },
    { text: '> Activating firewall rules... [OK]', className: 'text-cyber-green', speed: 20, delay: 100 },
    { text: '> nmap -sS -sV -O 0.0.0.0/0 --reason', className: 'text-cyber-cyan/70', speed: 18, delay: 100 },
    { text: '> Scanning network perimeter... [SECURE]', className: 'text-cyber-green', speed: 20, delay: 100 },
    { text: '> openssl s_client -connect target:443 -showcerts', className: 'text-cyber-green/70', speed: 18, delay: 100 },
    { text: '> Mounting encrypted filesystem... [OK]', className: 'text-cyber-green', speed: 20, delay: 150 },
    { text: '> volatility -f memory.raw --profile=Win10x64 pslist', className: 'text-cyber-cyan/70', speed: 18, delay: 100 },
    { text: '> msfconsole -q -x "db_status"', className: 'text-cyber-purple/70', speed: 18, delay: 100 },
    { text: '> System ready. All defenses online.', className: 'text-cyber-cyan', speed: 25, delay: 400 },
    { text: '', className: 'text-cyber-green', speed: 1, delay: 150 },
    { text: 'root@arapbett:~$ whoami', className: 'text-cyber-green', speed: 50, delay: 300 },
    { text: 'Kipkorir Arap Bett', className: 'text-white text-lg md:text-xl font-bold', speed: 40, delay: 250 },
    { text: 'root@arapbett:~$ cat /etc/specialization.conf', className: 'text-cyber-green', speed: 40, delay: 300 },
    { text: 'Cybersecurity Specialist | Penetration Tester | Security Engineer', className: 'text-cyber-cyan', speed: 25, delay: 400 },
  ];

  useEffect(() => {
    if (bootComplete) {
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    }
  }, [bootComplete]);

  // Shuffled commands for background display
  const bgCommands = useMemo(() => {
    const nmapCmds = getCommandsByCategory('nmap');
    const msfCmds = getCommandsByCategory('metasploit');
    const netCmds = getCommandsByCategory('network');
    return [...nmapCmds, ...msfCmds, ...netCmds].sort(() => Math.random() - 0.5);
  }, []);

  const services = [
    { icon: FaBug, title: 'Penetration Testing', desc: 'Advanced offensive security assessments to identify vulnerabilities before adversaries do.' },
    { icon: FaShieldAlt, title: 'Incident Response', desc: 'Rapid threat containment, forensic analysis, and recovery operations.' },
    { icon: FaLock, title: 'Security Architecture', desc: 'Design and implementation of defense-in-depth security infrastructure.' },
    { icon: FaNetworkWired, title: 'Network Security', desc: 'Firewall deployment, IDS/IPS tuning, and network traffic analysis.' },
    { icon: FaCode, title: 'Secure Development', desc: 'Security-first application development with OWASP compliance.' },
    { icon: FaServer, title: 'Cloud Security', desc: 'Hardening cloud infrastructure across AWS, Azure, and GCP environments.' },
  ];

  return (
    <div className="relative min-h-screen">
      {/* Floating background icons */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <FloatingIcon icon={FaShieldAlt} delay={0} x={10} y={20} />
        <FloatingIcon icon={FaRobot} delay={1} x={80} y={15} />
        <FloatingIcon icon={FaSkull} delay={2} x={70} y={60} />
        <FloatingIcon icon={FaLock} delay={3} x={15} y={70} />
        <FloatingIcon icon={FaBug} delay={1.5} x={85} y={40} />
        <FloatingIcon icon={FaNetworkWired} delay={2.5} x={5} y={45} />
        <FloatingIcon icon={FaTerminal} delay={0.5} x={50} y={80} />
        <FloatingIcon icon={FaCode} delay={3.5} x={40} y={10} />
      </div>

      {/* ===== HERO SECTION ===== */}
      <section className="min-h-screen flex items-center justify-center relative px-4 pt-16">
        {/* Background command waterfall */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none command-fade-top">
          <div className="flex h-full opacity-[0.04]">
            {[0, 1, 2].map((col) => (
              <div key={col} className="flex-1 overflow-hidden">
                <div
                  className="font-mono text-[10px] text-cyber-green leading-loose whitespace-nowrap"
                  style={{ animation: `scrollUp ${50 + col * 15}s linear infinite`, paddingTop: `${col * 300}px` }}
                >
                  {[...bgCommands, ...bgCommands].map((cmd, i) => (
                    <div key={i} className="truncate px-2">$ {cmd}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-4xl w-full relative z-10">
          {/* Terminal Boot Sequence */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="terminal-window glow-green mb-8"
          >
            <div className="terminal-header">
              <div className="terminal-dot bg-cyber-red"></div>
              <div className="terminal-dot bg-cyber-amber"></div>
              <div className="terminal-dot bg-cyber-green"></div>
              <span className="ml-3 text-xs text-cyber-muted">root@arapbett — bash — 80×24</span>
            </div>
            <div className="terminal-body min-h-[340px] md:min-h-[380px] bg-cyber-black/50">
              <TypingText lines={bootLines} onComplete={() => setBootComplete(true)} />
            </div>
          </motion.div>

          {/* CTA Buttons */}
          {showContent && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/portfolio" className="px-8 py-3 bg-cyber-green text-cyber-black font-bold rounded text-center hover:bg-cyber-cyan transition-all glow-green-strong hover:shadow-lg hover:shadow-cyber-green/30">
                {'>'} View My Work
              </Link>
              <Link to="/contact" className="px-8 py-3 border border-cyber-green text-cyber-green font-bold rounded text-center hover:bg-cyber-green/10 transition-all">
                {'>'} Establish Contact
              </Link>
              <Link to="/blog" className="px-8 py-3 border border-cyber-cyan text-cyber-cyan font-bold rounded text-center hover:bg-cyber-cyan/10 transition-all">
                {'>'} Read Dispatches
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* ===== COMMAND TICKER ===== */}
      <div className="py-3 border-y border-cyber-green/10 bg-cyber-dark/50 overflow-hidden command-fade-sides">
        <CommandTicker speed={80} />
      </div>

      {/* ===== LIVE SOC DASHBOARD ===== */}
      <section className="py-16 px-4 relative">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-sm font-mono text-cyber-green mb-2">{'// SECURITY OPERATIONS CENTER — LIVE FEED'}</h2>
            <div className="w-20 h-px bg-cyber-green mx-auto"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="terminal-window glow-green">
              <div className="terminal-header">
                <div className="terminal-dot bg-cyber-red"></div>
                <div className="terminal-dot bg-cyber-amber"></div>
                <div className="terminal-dot bg-cyber-green"></div>
                <span className="ml-3 text-xs text-cyber-muted">soc_monitor.sh — live threat analysis</span>
                <span className="ml-auto text-xs text-cyber-green animate-pulse">● RECORDING</span>
              </div>
              <div className="terminal-body bg-cyber-black/80 min-h-[300px] max-h-[400px] overflow-hidden">
                <LiveCommandFeed count={18} interval={1800} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== STATS SECTION ===== */}
      <section className="py-20 px-4 relative">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-sm font-mono text-cyber-green mb-2">{'// SYSTEM METRICS'}</h2>
            <div className="w-20 h-px bg-cyber-green mx-auto"></div>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <StatCard value="5+" label="Years Experience" icon={FaShieldAlt} delay={0} />
            <StatCard value="50+" label="Projects Secured" icon={FaLock} delay={0.1} />
            <StatCard value="100+" label="Vulnerabilities Found" icon={FaBug} delay={0.2} />
            <StatCard value="24/7" label="Threat Monitoring" icon={FaNetworkWired} delay={0.3} />
          </div>
        </div>
      </section>

      {/* ===== SERVICES WITH COMMAND BACKGROUNDS ===== */}
      <section className="py-20 px-4 bg-cyber-dark/50 relative overflow-hidden">
        {/* Background commands */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] overflow-hidden command-fade-top">
          <div className="font-mono text-[9px] text-cyber-green leading-relaxed p-4 columns-3 gap-8">
            {allCommands.slice(0, 120).map((cmd, i) => (
              <div key={i} className="break-inside-avoid mb-0.5">$ {cmd}</div>
            ))}
          </div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-sm font-mono text-cyber-green mb-2">{'// CAPABILITIES'}</h2>
            <p className="text-2xl md:text-3xl font-bold text-white mt-4">
              Security <span className="text-cyber-green">Services</span> & <span className="text-cyber-cyan">Expertise</span>
            </p>
            <div className="w-20 h-px bg-cyber-green mx-auto mt-4"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="cyber-card p-6 group"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded bg-cyber-green/10 border border-cyber-green/20 group-hover:border-cyber-green/40 transition-colors">
                    <service.icon className="text-xl text-cyber-green" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{service.title}</h3>
                </div>
                <p className="text-sm text-cyber-muted leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== NETWORK VISUALIZATION ===== */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-sm font-mono text-cyber-green mb-6">{'// THREAT LANDSCAPE'}</h2>
            <div className="relative h-64 md:h-80 mb-8">
              <motion.div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full border-2 border-cyber-green flex items-center justify-center z-10 bg-cyber-dark"
                animate={{ boxShadow: ['0 0 0 0 rgba(0,255,65,0.4)', '0 0 0 20px rgba(0,255,65,0)', '0 0 0 0 rgba(0,255,65,0.4)'] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <FaShieldAlt className="text-cyber-green text-2xl" />
              </motion.div>
              {[
                { icon: FaRobot, label: 'BOT', angle: 0, radius: 120, color: 'cyber-cyan' },
                { icon: FaBug, label: 'CVE', angle: 60, radius: 130, color: 'cyber-red' },
                { icon: FaLock, label: 'ENC', angle: 120, radius: 110, color: 'cyber-green' },
                { icon: FaServer, label: 'SRV', angle: 180, radius: 125, color: 'cyber-purple' },
                { icon: FaNetworkWired, label: 'NET', angle: 240, radius: 115, color: 'cyber-cyan' },
                { icon: FaSkull, label: 'APT', angle: 300, radius: 135, color: 'cyber-red' },
              ].map((node, i) => {
                const rad = (node.angle * Math.PI) / 180;
                const x = Math.cos(rad) * node.radius;
                const y = Math.sin(rad) * node.radius;
                return (
                  <motion.div
                    key={node.label}
                    className="absolute left-1/2 top-1/2 flex flex-col items-center gap-1"
                    style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 3, delay: i * 0.5, repeat: Infinity }}
                  >
                    <div className={`w-10 h-10 rounded border border-${node.color}/50 flex items-center justify-center bg-cyber-dark/80`}>
                      <node.icon className={`text-${node.color}`} />
                    </div>
                    <span className={`text-xs text-${node.color} font-mono`}>{node.label}</span>
                  </motion.div>
                );
              })}
              <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
                {[0, 60, 120, 180, 240, 300].map((angle, i) => {
                  const rad = (angle * Math.PI) / 180;
                  return (
                    <line key={i} x1="50%" y1="50%" x2={`${50 + Math.cos(rad) * 38}%`} y2={`${50 + Math.sin(rad) * 38}%`}
                      stroke="rgba(0,255,65,0.15)" strokeWidth="1" strokeDasharray="4 4" />
                  );
                })}
              </svg>
            </div>
            <p className="text-cyber-muted text-sm max-w-lg mx-auto">
              Continuous monitoring and defense against evolving cyber threats.
              From APT groups to automated botnets — every vector covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== RECON TERMINAL — REAL COMMANDS DISPLAY ===== */}
      <section className="py-16 px-4 bg-cyber-dark/30">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-8">
            <h2 className="text-sm font-mono text-cyber-green mb-2">{'// RECONNAISSANCE TOOLKIT SAMPLE'}</h2>
            <div className="w-20 h-px bg-cyber-green mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'nmap_scan.sh', cmds: getCommandsByCategory('nmap').slice(0, 8) },
              { title: 'metasploit_ops.rc', cmds: getCommandsByCategory('metasploit').slice(0, 8) },
              { title: 'wireless_audit.sh', cmds: getCommandsByCategory('wireless').slice(0, 8) },
              { title: 'forensics_workflow.sh', cmds: getCommandsByCategory('forensics').slice(0, 8) },
              { title: 'password_crack.sh', cmds: getCommandsByCategory('passwords').slice(0, 8) },
              { title: 'cloud_recon.sh', cmds: getCommandsByCategory('cloud').slice(0, 8) },
            ].map((block, i) => (
              <motion.div
                key={block.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
              >
                <div className="terminal-window h-full">
                  <div className="terminal-header">
                    <div className="terminal-dot bg-cyber-red"></div>
                    <div className="terminal-dot bg-cyber-amber"></div>
                    <div className="terminal-dot bg-cyber-green"></div>
                    <span className="ml-3 text-xs text-cyber-muted">{block.title}</span>
                  </div>
                  <div className="terminal-body text-xs bg-cyber-black/40 p-3">
                    {block.cmds.map((cmd, j) => (
                      <div key={j} className="text-cyber-green/70 truncate mb-0.5">
                        <span className="text-cyber-cyan/50">$</span> {cmd}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-20 px-4 bg-cyber-dark/50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="terminal-window glow-green">
            <div className="terminal-header">
              <div className="terminal-dot bg-cyber-red"></div>
              <div className="terminal-dot bg-cyber-amber"></div>
              <div className="terminal-dot bg-cyber-green"></div>
              <span className="ml-3 text-xs text-cyber-muted">mission_brief.sh</span>
            </div>
            <div className="terminal-body text-center py-10">
              <p className="text-cyber-green text-sm mb-4">{'// READY TO SECURE YOUR SYSTEMS?'}</p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Let's Build Your <span className="text-cyber-green">Defense</span>
              </h3>
              <Link
                to="/contact"
                className="inline-block px-8 py-3 bg-cyber-green text-cyber-black font-bold rounded hover:bg-cyber-cyan transition-all glow-green-strong"
              >
                {'>'} Initialize Contact Protocol
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ===== BOTTOM TICKER ===== */}
      <div className="py-2 border-t border-cyber-green/10 bg-cyber-dark/30 overflow-hidden command-fade-sides">
        <CommandTicker speed={100} />
      </div>
    </div>
  );
};

export default Home;
